import { debounceTime, distinctUntilChanged, filter, ignoreElements, map, switchMap, tap } from "rxjs/operators";
import { simpleStableHash } from "@package/util/hash";
import type { GraphQLClient } from "@package/graphql";
import { combineLatest, EMPTY, from, merge, type Observable } from "rxjs";
import { logger } from "@artis/logger";

import type { TProduct, TProductConfig } from "../../../../../data";
import { PageProductIdsSub } from "./queries";
import type * as Domain from "../../domain";
import { observe } from "../../util";

export type ProductChangeType = "formula" | "metadata" | "both" | "curve-moved" | "permissions-changed";
/**
 * `gained`/`lost` mark the folder becoming visible or invisible for the first time; every other
 * move between visible levels is `upgraded`/`downgraded`/`restricted`, named by `fromAccess`/
 * `toAccess` rather than by the direction itself. `changed` only remains for older persisted
 * alerts predating a strict access ladder.
 */
export type PermissionChangeDirection = "gained" | "lost" | "upgraded" | "downgraded" | "restricted" | "changed";

/** Permission directions that leave the folder visible, so its name and curves can be shown. */
export type VisiblePermissionChangeDirection = Exclude<PermissionChangeDirection, "lost">;

export type ProductChangeAlert = {
  name: string;
  productId?: string;
  /** Package (folder) id when the alert was raised — keeps same-name curves in different folders distinct. */
  packageId?: string | null;
  changeType: ProductChangeType;
  /** Whether usable folder access was granted or removed. Older persisted alerts default to lost. */
  permissionChangeDirection?: PermissionChangeDirection;
  /** Folder access before the change, so a card can name the exact levels ("from Read to List"). */
  fromAccess?: FolderAccess;
  /** Folder access after the change — lets "gained" copy say "view" instead of "view and use" when it only landed on list. */
  toAccess?: FolderAccess;
  /** Curves in the folder when the change was detected — the alerts themselves are capped. */
  folderCurveCount?: number;
  /** Short user-facing line shown under the curve name in toast / sidebar. */
  detail?: string;
  /** Metadata field labels that changed (used to build detail copy). */
  changedFieldLabels?: string[];
  /** Source folder when a curve moved — used in card titles so we don't show a destination the user can't see. */
  fromFolderName?: string | null;
  /** Move copy kept when a later formula/metadata edit is merged onto the same toast row. */
  moveDetail?: string;
  /** When this row first entered the sidebar — lets the panel show the newest notifications first. */
  createdAt?: number;
};

export const isAccessChangeType = (changeType: ProductChangeType) =>
  changeType === "curve-moved" || changeType === "permissions-changed";

export const isPermissionChangeType = (changeType: ProductChangeType) => changeType === "permissions-changed";

/** Formula, metadata, both, and curve moves — shown under Curve changes in the notifications panel. */
export const isCurveChangeType = (changeType: ProductChangeType) => !isPermissionChangeType(changeType);

export const productChangeAlertKey = (alert: ProductChangeAlert) =>
  `${alert.productId ?? alert.name}:${alert.changeType}`;

export const isFormulaMetadataChangeType = (changeType: ProductChangeType) =>
  changeType === "formula" || changeType === "metadata" || changeType === "both";

/** One occurrence of a stacked card — sidebar key plus the arrival time it was stamped with. */
export const productChangeOccurrenceKey = (alert: ProductChangeAlert) =>
  `${productChangeSidebarKey(alert)}#${alert.createdAt ?? 0}`;

/** Sidebar + toast dedupe: never merge by display name — productId + package + change bucket. */
export const productChangeSidebarKey = (
  alert: Pick<
    ProductChangeAlert,
    "productId" | "name" | "changeType" | "packageId" | "permissionChangeDirection" | "fromAccess" | "toAccess"
  >,
) => {
  const packageSuffix = alert.packageId != null && alert.packageId !== "" ? `:${alert.packageId}` : "";
  // Distinct pairs (e.g. list -> read, read -> broadcast) can share a direction, so key on the
  // exact levels too.
  const permissionSuffix =
    alert.changeType === "permissions-changed"
      ? `:${alert.permissionChangeDirection ?? "lost"}:${alert.fromAccess ?? "-"}:${alert.toAccess ?? "-"}`
      : "";
  const bucket = isAccessChangeType(alert.changeType) ? `${alert.changeType}${permissionSuffix}` : "formula-metadata";

  if (alert.productId) {
    return `${alert.productId}${packageSuffix}:${bucket}`;
  }

  const base = alert.name.trim().toLocaleLowerCase();
  return `${base}${packageSuffix}:${bucket}`;
};

/** Only rows sharing the same batch (no `createdAt` yet) or the exact same occurrence collapse. */
const collapseKey = (alert: ProductChangeAlert) =>
  alert.createdAt != null ? `${productChangeSidebarKey(alert)}#${alert.createdAt}` : productChangeSidebarKey(alert);

/**
 * Collapse duplicate rows (e.g. separate formula + both entries for the same curve) that arrived
 * in the same batch and haven't been stamped with a `createdAt` yet. Once a row has been through
 * {@link mergeProductChangeAlertRows} and carries a `createdAt`, that timestamp is folded into the
 * key too — otherwise this would re-merge distinct edits (each its own card) back into one the
 * next time the panel re-renders.
 */
export const collapseProductChangeAlerts = (alerts: ProductChangeAlert[]): ProductChangeAlert[] => {
  const merged = new Map<string, ProductChangeAlert>();

  for (const item of alerts) {
    const key = collapseKey(item);
    const prev = merged.get(key);
    if (!prev) {
      merged.set(key, item);
      continue;
    }

    if (prev.productId && item.productId && prev.productId !== item.productId) {
      merged.set(`${key}#${item.productId}`, item);
      continue;
    }

    merged.set(key, {
      ...prev,
      ...item,
      productId: item.productId ?? prev.productId,
      name: item.name || prev.name,
      packageId: item.packageId ?? prev.packageId,
      changeType: mergeFormulaMetadataChangeType(prev.changeType, item.changeType),
      detail: item.detail ?? prev.detail,
      changedFieldLabels: item.changedFieldLabels ?? prev.changedFieldLabels,
    });
  }

  return [...merged.values()];
};

export const lostFolderPackageIds = (alerts: ProductChangeAlert[]): string[] =>
  alerts
    .filter((item) => item.changeType === "permissions-changed" && item.permissionChangeDirection === "lost")
    .map((item) => item.packageId)
    .filter((packageId): packageId is string => Boolean(packageId));

/**
 * Formula/metadata edits stack — each genuinely new save gets its own card, the same way each one
 * raises its own toast. Permission and folder-move changes still update a single row in place
 * (re-losing/re-gaining the same folder bumps that one card to the top rather than piling up).
 */
const stacksNewCards = (changeType: ProductChangeType) => isFormulaMetadataChangeType(changeType);

/**
 * Storage key for a row already in the sidebar list. Stackable rows are keyed with their
 * `createdAt` so distinct edits never collide; everything else keeps the plain bucket key so a
 * later change updates the same row.
 */
const storedRowKey = (alert: ProductChangeAlert) => {
  const key = productChangeSidebarKey(alert);
  return stacksNewCards(alert.changeType) ? `${key}#${alert.createdAt ?? 0}` : key;
};

/**
 * Merge new alerts into sidebar state. Within {@link PARTIAL_FORMULA_METADATA_MS}, combine
 * formula + metadata from split subscription ticks into the still-open card; a repeat outside
 * that window is a new edit — stackable types (formula/metadata) get a fresh card, everything
 * else updates its existing row with a fresh `createdAt`.
 */
export const mergeProductChangeAlertRows = (
  existing: ProductChangeAlert[],
  incoming: ProductChangeAlert[],
  lastChangeAtByKey: Map<string, number>,
  now = Date.now(),
): ProductChangeAlert[] => {
  // Curve changes for a folder just lost are dropped rather than kept under a placeholder name.
  const lostFolderKeys = new Set(lostFolderPackageIds(incoming));
  const isPurged = (item: ProductChangeAlert) =>
    isCurveChangeType(item.changeType) && Boolean(item.packageId) && lostFolderKeys.has(item.packageId as string);

  const merged = new Map<string, ProductChangeAlert>();

  for (const item of existing) {
    if (isPurged(item)) continue;
    merged.set(storedRowKey(item), item);
  }

  /** For a stackable key, the still-open card to combine into (the most recently created one). */
  const findOpenOccurrence = (key: string): [string, ProductChangeAlert] | undefined => {
    let best: [string, ProductChangeAlert] | undefined;
    for (const [storageKey, alert] of merged) {
      if (productChangeSidebarKey(alert) !== key) continue;
      if (!best || (alert.createdAt ?? 0) > (best[1].createdAt ?? 0)) best = [storageKey, alert];
    }
    return best;
  };

  for (const item of incoming) {
    if (isPurged(item)) continue;
    const key = productChangeSidebarKey(item);
    const lastAt = lastChangeAtByKey.get(key) ?? 0;
    const combineSplitStreams = now - lastAt <= PARTIAL_FORMULA_METADATA_MS;
    lastChangeAtByKey.set(key, now);

    const stackable = stacksNewCards(item.changeType);
    const found: [string, ProductChangeAlert] | undefined = stackable
      ? combineSplitStreams
        ? findOpenOccurrence(key)
        : undefined
      : merged.has(key)
        ? [key, merged.get(key) as ProductChangeAlert]
        : undefined;
    const [prevKey, prev] = found ?? [undefined, undefined];

    if (!prev) {
      const newKey = stackable ? `${key}#${now}` : key;
      merged.set(newKey, { ...item, createdAt: item.createdAt ?? now });
      continue;
    }

    if (prev.productId && item.productId && prev.productId !== item.productId) {
      merged.set(`${key}#${item.productId}`, { ...item, createdAt: item.createdAt ?? now });
      continue;
    }

    merged.set(prevKey as string, {
      ...prev,
      ...item,
      productId: item.productId ?? prev.productId,
      name: item.name || prev.name,
      packageId: item.packageId ?? prev.packageId,
      changeType: combineSplitStreams
        ? mergeFormulaMetadataChangeType(prev.changeType, item.changeType)
        : item.changeType,
      detail: item.detail ?? prev.detail,
      changedFieldLabels: item.changedFieldLabels ?? prev.changedFieldLabels,
      createdAt: combineSplitStreams ? (prev.createdAt ?? now) : now,
    });
  }

  return [...merged.values()];
};

/** Toast batching key — one toast per curve while split-stream updates settle. */
export const productChangeSessionKey = (alert: Pick<ProductChangeAlert, "productId" | "name" | "packageId">) => {
  if (alert.productId) return alert.productId;
  const packageSuffix = alert.packageId != null && alert.packageId !== "" ? `:${alert.packageId}` : "";
  return `${alert.name.trim().toLocaleLowerCase()}${packageSuffix}`;
};

export const mergeFormulaMetadataChangeType = (
  current: ProductChangeType,
  next: ProductChangeType,
): ProductChangeType => {
  if (current === "both" || next === "both") return "both";
  if (current === next) return current;
  if ((current === "formula" || current === "metadata") && (next === "formula" || next === "metadata")) {
    return "both";
  }
  return current;
};

/** How long to remember a formula-only (or metadata-only) tick while waiting for the paired stream update. */
export const PARTIAL_FORMULA_METADATA_MS = 3_000;

export type PartialFormulaMetadataEntry = { formula: boolean; metadata: boolean; at: number };

/**
 * Product info and configs often update in separate subscription ticks after one save.
 * Remember partial changes so a later tick can emit `both` instead of leaving formula-only.
 */
export const resolvePartialFormulaMetadataChangeType = (
  partialMap: Map<string, PartialFormulaMetadataEntry>,
  key: string,
  flags: { formula: boolean; metadata: boolean },
  now = Date.now(),
): ProductChangeType | null => {
  if (!flags.formula && !flags.metadata) return null;

  const existing = partialMap.get(key);
  const expired = !existing || now - existing.at > PARTIAL_FORMULA_METADATA_MS;
  const partial: PartialFormulaMetadataEntry = expired
    ? { formula: false, metadata: false, at: now }
    : { ...existing, at: now };

  if (flags.formula) partial.formula = true;
  if (flags.metadata) partial.metadata = true;

  const changeType: ProductChangeType =
    partial.formula && partial.metadata ? "both" : partial.formula ? "formula" : "metadata";

  if (changeType === "both") {
    partialMap.delete(key);
  } else {
    partialMap.set(key, partial);
  }

  return changeType;
};

export const getProductChangeToastTitle = (changeType: ProductChangeType) => {
  switch (changeType) {
    case "curve-moved":
      return "Curve moved";
    case "permissions-changed":
      return "Permissions changed";
    case "formula":
      return "Product formula changed";
    case "metadata":
      return "Product metadata changed";
    case "both":
      return "Product formula and metadata changed";
  }
};

export const buildAccessChangeDetail = (
  changeType: "curve-moved" | "permissions-changed",
  folders: {
    fromFolderName?: string | null;
    toFolderName?: string | null;
    originalFolderName?: string | null;
    /** Only set when the stream reported a different package id (avoid stale "moved to" same folder). */
    confirmedDestination?: boolean;
    permissionChangeDirection?: PermissionChangeDirection;
  },
): string => {
  if (changeType === "curve-moved") {
    const from = folders.fromFolderName?.trim();
    const to = folders.toFolderName?.trim();
    const sameFolder = Boolean(from && to && from.localeCompare(to, undefined, { sensitivity: "accent" }) === 0);

    if (folders.confirmedDestination && to && !sameFolder) {
      // Origin unnamed when the user cannot see it: "into" the destination they can.
      return from ? `Moved from ${from} to ${to}` : `Moved into ${to}`;
    }
    if (from) return `Moved from ${from} to a new Folder`;
    return "Moved to a new Folder";
  }

  const folderName = folders.originalFolderName ?? folders.fromFolderName;
  if (folders.permissionChangeDirection === "gained") {
    return folderName ? `Access granted for folder "${folderName}"` : "You now have access to this curve";
  }
  if (folders.permissionChangeDirection === "upgraded") {
    return folderName ? `Edit access granted for folder "${folderName}"` : "You can now edit this curve";
  }
  if (folders.permissionChangeDirection === "downgraded") {
    return folderName
      ? `Access is now read-only for folder "${folderName}"`
      : "Your access to this curve is now read-only";
  }
  if (folders.permissionChangeDirection === "restricted") {
    return folderName ? `Access is now list-only for folder "${folderName}"` : "You can no longer view this curve";
  }
  if (folders.permissionChangeDirection === "changed") {
    return folderName
      ? `Permission type changed for folder "${folderName}"`
      : "Your permission type for this curve has changed";
  }
  // Access removed: the folder is no longer visible to this user, and we deliberately don't name
  // it (or the curve) anywhere the card or the persisted sidebar state could surface it.
  return "Your access to one or more folders was removed";
};

export type CurveAccessChangeReason = "curve-moved" | "permissions-changed" | "removed-from-subscription" | "ambiguous";

/** Page curve left the worker stream — refined on the main thread for access alerts. */
export type CurveFolderDebugPayload = {
  accessChangeReason: CurveAccessChangeReason;
  curveName: string | null;
  productId: string;
  folderAccessChange?: {
    from: FolderAccess;
    to: FolderAccess;
  };
  lastKnownFolder?: { packageId: string | null; folderName: string | null } | null;
  note?: string;
};

export type ProductAlertsTick = {
  /**
   * Present when the tick contains folder-level permission alerts. Persists the folder baseline
   * the alerts were detected against; the runner calls this once the batch has been acked by the
   * main thread. {@link detectFolderAccessChanges} still updates the in-memory baseline
   * immediately — only the IndexedDB write is deferred. A later info tick that produces no folder
   * alerts saves right away (see the no-alerts branch in {@link createSource}), ack or no ack, so
   * the deferral mainly covers the window before the next info update, not indefinitely. Until
   * either path persists, a worker restart can re-detect against the old stored baseline; the
   * sidebar's key-based merge makes redelivery idempotent.
   */
  commitFolderSnapshot?: () => void;
  alerts: ProductChangeAlert[];
  debug: CurveFolderDebugPayload[];
};

type ProductChangeAlertsContext = {
  /** Current page columns (from main thread). */
  pageProducts$: Observable<Set<string>>;
  /** Fallback for tests / until page-products message is received. */
  graphql$: Observable<GraphQLClient>;
  info: Observable<Domain.ProductInfo>;
  configs: Observable<Domain.ProductConfigs>;
  user: Observable<{ id: string }>;
  /** Omitted in tests — detection then only compares within the current session. */
  folderSnapshots?: FolderSnapshotStore;
  /** Product ids this session just saved — suppress own formula/metadata/move cards until expiry. */
  expectProductChanges$?: Observable<{ productIds: string[]; at: number }>;
};

/** How long a local save suppresses the editor's own curve-change cards. Split formula/metadata ticks need this window. */
export const SELF_EDIT_SUPPRESS_MS = 30_000;

type HashEntry = {
  meta: string;
  metadataSnapshot: MetadataSnapshot;
  formula: string;
  package: string | null;
  folderName: string | null;
  name: string | null;
  folderAccess: FolderAccess;
  /**
   * Formula hash is not comparable until configs are visible with usable access. List (and a
   * delayed config subscription after an upgrade) must not dump the current formula as an edit.
   */
  awaitingUsableFormulaBaseline?: boolean;
};

type FolderCurve = { id: string; name: string };

export type FolderAccessEntry = {
  folderName: string | null;
  access: FolderAccess;
  /** Capped at {@link MAX_CURVE_ALERTS_PER_FOLDER} — one alert per curve is emitted from these. */
  curves: FolderCurve[];
  /** Every curve id in the folder, used to tell a move (curves reappear elsewhere) from a revoke. */
  curveIds: string[];
  /** Every curve in the folder, so a card can say "and 197 others" despite the cap above. */
  curveCount: number;
};

/** Folders the user can currently see, keyed by package id — rebuilt from `info` each tick. */
export type FolderAccessSnapshot = Map<string, FolderAccessEntry>;

/**
 * The comparison baseline: access level per package id, and nothing else. Folder and curve names
 * are deliberately absent — a card for a folder the user has lost never names it, and the ones
 * that do name a folder are for folders still present in `info`, so they read names live.
 */
export type FolderAccessBaseline = Map<string, FolderAccess>;

/**
 * Persists the folder baseline between sessions. Without it a restarted worker treats whatever
 * it boots with as the baseline, so a permission change made while the tab was closed — or
 * during a reload — is never reported.
 */
export type FolderSnapshotStore = {
  load: (userId: string) => Promise<FolderAccessBaseline | null>;
  save: (userId: string, baseline: FolderAccessBaseline) => Promise<void>;
};

/** Highest access wins if products within one folder ever disagree. Write outranks broadcast (adds edit). */
const FOLDER_ACCESS_RANK: Record<FolderAccess, number> = {
  none: 0,
  other: 1,
  list: 2,
  read: 3,
  broadcast: 4,
  write: 5,
};

/**
 * Per-curve alerts emitted for a single folder transition are capped: cards only ever name the
 * first few curves plus a count, so a very large folder does not need an unbounded payload.
 * Kept small because these alerts are persisted in the sidebar atom (localStorage); the true
 * total travels separately as {@link ProductChangeAlert.folderCurveCount}.
 */
export const MAX_CURVE_ALERTS_PER_FOLDER = 25;

export const buildFolderAccessSnapshot = (info: Domain.ProductInfo): FolderAccessSnapshot => {
  const folders: FolderAccessSnapshot = new Map();

  for (const product of Object.values(info)) {
    if (product.package == null) continue;
    const packageId = String(product.package);
    const access = getProductFolderAccess(product);
    const existing = folders.get(packageId);

    if (!existing) {
      folders.set(packageId, {
        folderName: product.packageByPackage?.name ?? null,
        access,
        curves: [{ id: product.id, name: product.name }],
        curveIds: [product.id],
        curveCount: 1,
      });
      continue;
    }

    existing.curveCount += 1;
    existing.curveIds.push(product.id);
    if (existing.curves.length < MAX_CURVE_ALERTS_PER_FOLDER) {
      existing.curves.push({ id: product.id, name: product.name });
    }
    if (FOLDER_ACCESS_RANK[access] > FOLDER_ACCESS_RANK[existing.access]) {
      existing.access = access;
      existing.folderName = product.packageByPackage?.name ?? existing.folderName;
    }
  }

  return folders;
};

/**
 * One alert per folder, carrying no curve and no name: the card only reports how many folders
 * were affected, and anything named here would be persisted alongside the sidebar state.
 */
const folderRemovedAlert = (packageId: string | null, fromAccess: FolderAccess): ProductChangeAlert => ({
  name: "",
  packageId,
  changeType: "permissions-changed",
  permissionChangeDirection: "lost",
  fromAccess,
  detail: buildAccessChangeDetail("permissions-changed", { permissionChangeDirection: "lost" }),
});

const curveMovedIntoFolderAlert = (
  curve: { id: string; name: string },
  packageId: string,
  folderName: string | null,
): ProductChangeAlert => ({
  productId: curve.id,
  name: curve.name,
  packageId,
  changeType: "curve-moved",
  fromFolderName: null,
  detail: buildAccessChangeDetail("curve-moved", {
    toFolderName: folderName,
    confirmedDestination: true,
  }),
});

const folderAccessAlerts = (
  entry: FolderAccessEntry,
  packageId: string,
  permissionChangeDirection: PermissionChangeDirection,
  fromAccess: FolderAccess,
): ProductChangeAlert[] => {
  if (permissionChangeDirection === "lost") {
    return [folderRemovedAlert(packageId, fromAccess)];
  }

  const detail = buildAccessChangeDetail("permissions-changed", {
    fromFolderName: entry.folderName,
    originalFolderName: entry.folderName,
    permissionChangeDirection,
  });

  const curves = entry.curves.slice(0, MAX_CURVE_ALERTS_PER_FOLDER);
  if (curves.length === 0) {
    return [
      {
        name: "",
        packageId,
        changeType: "permissions-changed" as const,
        permissionChangeDirection,
        fromAccess,
        toAccess: entry.access,
        folderCurveCount: entry.curveCount,
        detail,
      },
    ];
  }

  return curves.map((curve) => ({
    productId: curve.id,
    name: curve.name,
    packageId,
    changeType: "permissions-changed" as const,
    permissionChangeDirection,
    fromAccess,
    toAccess: entry.access,
    folderCurveCount: entry.curveCount,
    detail,
  }));
};

export const diffFolderAccessSnapshots = (
  lastKnown: FolderAccessBaseline,
  next: FolderAccessSnapshot,
  established = lastKnown.size > 0,
): ProductChangeAlert[] => {
  if (!established) return [];

  const alerts: ProductChangeAlert[] = [];
  const reported: { packageId: string; from: FolderAccess; to: FolderAccess; known: boolean }[] = [];

  for (const [packageId, entry] of next) {
    const previousAccess = lastKnown.get(packageId) ?? "none";
    const direction = getPermissionChangeDirection(previousAccess, entry.access);
    if (!direction) continue;
    alerts.push(...folderAccessAlerts(entry, packageId, direction, previousAccess));
    // `known: false` means the folder was absent from the baseline rather than genuinely
    // changing level — a repeat of that across reloads is a snapshot problem, not a real grant.
    reported.push({ packageId, from: previousAccess, to: entry.access, known: lastKnown.has(packageId) });
  }

  for (const [packageId, previousAccess] of lastKnown) {
    if (next.has(packageId)) continue;
    const direction = getPermissionChangeDirection(previousAccess, "none");
    if (!direction) continue;
    alerts.push(folderRemovedAlert(packageId, previousAccess));
    reported.push({ packageId, from: previousAccess, to: "none", known: true });
  }

  if (reported.length > 0) {
    logger.info("Folder access changes detected", { tags: ["calc-worker"], baseline: lastKnown.size, reported });
  }

  return alerts;
};

export const applyFolderAccessSnapshot = (lastKnown: FolderAccessBaseline, next: FolderAccessSnapshot): void => {
  lastKnown.clear();
  for (const [packageId, entry] of next) lastKnown.set(packageId, entry.access);
};

/**
 * Detect folder-level access changes across every product the user can see — not just curves on
 * their pages — so gaining or losing access to a whole folder is reported even when none of its
 * curves are on a page. Losing all access hides the rows entirely, so a folder vanishing from
 * `info` counts as a removal; that card names nothing, so the baseline needs no names either.
 *
 * The first baseline only establishes a reference point; it never raises alerts.
 */
export const detectFolderAccessChanges = (
  lastKnown: FolderAccessBaseline,
  info: Domain.ProductInfo,
  lastKnownCurves: Map<string, string[]> = new Map(),
  selfEdits?: ReadonlySet<string>,
  seenCurveIds?: Set<string>,
): ProductChangeAlert[] => {
  const next = buildFolderAccessSnapshot(info);
  const alerts: ProductChangeAlert[] = [];
  const isBaseline = lastKnown.size === 0;
  const reported: { packageId: string; from: FolderAccess; to: FolderAccess; known: boolean }[] = [];
  const curveToPackage = new Map<string, string>();
  const previouslySeenCurveIds = seenCurveIds ?? new Set<string>();
  if (!seenCurveIds) {
    for (const ids of lastKnownCurves.values()) {
      for (const id of ids) previouslySeenCurveIds.add(id);
    }
  }
  for (const [packageId, entry] of next) {
    for (const id of entry.curveIds) curveToPackage.set(id, packageId);
  }

  /** Last visible curve(s) in this folder now live in other folders we can still see — a move, not a revoke. */
  const folderEmptiedByMove = (packageId: string) => {
    const previousIds = lastKnownCurves.get(packageId) ?? [];
    if (previousIds.length === 0) return false;
    return previousIds.every((id) => {
      const now = curveToPackage.get(id);
      return now != null && now !== packageId;
    });
  };

  /** Every curve now in this folder was already visible under a different package — they moved in. */
  const folderFilledByMove = (packageId: string, entry: FolderAccessEntry) => {
    if (entry.curveIds.length === 0) return false;
    return entry.curveIds.every((id) => {
      for (const [fromPkg, ids] of lastKnownCurves) {
        if (fromPkg !== packageId && ids.includes(id)) return true;
      }
      return false;
    });
  };

  /** Previously known curves are all still here; the folder only grew. */
  const onlyNewCurvesAdded = (packageId: string, entry: FolderAccessEntry) => {
    const previousIds = lastKnownCurves.get(packageId);
    if (!previousIds?.length) return false;
    const nextIds = new Set(entry.curveIds);
    return previousIds.every((id) => nextIds.has(id)) && entry.curveIds.length > previousIds.length;
  };

  /** Existing curves still report the baseline access — the delta came from the new arrivals. */
  const previousCurvesDidNotChangeAccess = (packageId: string, previousAccess: FolderAccess) => {
    const previousIds = lastKnownCurves.get(packageId) ?? [];
    return previousIds.every((id) => {
      const product = info[id];
      if (!product) return false;
      return getProductFolderAccess(product) === previousAccess;
    });
  };

  if (!isBaseline) {
    for (const [packageId, entry] of next) {
      const previousAccess = lastKnown.get(packageId) ?? "none";
      const direction = getPermissionChangeDirection(previousAccess, entry.access);
      if (!direction) continue;
      if (direction === "gained" && folderFilledByMove(packageId, entry)) continue;
      // Access looks like it changed only because a newly arrived curve reports a different
      // permission on the same package — the user's grant to this folder is not new.
      if (
        lastKnown.has(packageId) &&
        onlyNewCurvesAdded(packageId, entry) &&
        (direction === "gained" || direction === "upgraded") &&
        previousCurvesDidNotChangeAccess(packageId, previousAccess)
      ) {
        continue;
      }
      alerts.push(...folderAccessAlerts(entry, packageId, direction, previousAccess));
      // `known: false` means the folder was absent from the baseline rather than genuinely
      // changing level — a repeat of that across reloads is a snapshot problem, not a real grant.
      reported.push({ packageId, from: previousAccess, to: entry.access, known: lastKnown.has(packageId) });
    }

    for (const [packageId, previousAccess] of lastKnown) {
      if (next.has(packageId)) continue;
      const direction = getPermissionChangeDirection(previousAccess, "none");
      if (!direction) continue;
      if (folderEmptiedByMove(packageId)) continue;
      alerts.push(folderRemovedAlert(packageId, previousAccess));
      reported.push({ packageId, from: previousAccess, to: "none", known: true });
    }

    // A curve previously seen in another folder (including one that later became inaccessible)
    // appearing in a folder we already had is a move. A brand-new id is a create, not a move.
    for (const [packageId, entry] of next) {
      if (!lastKnown.has(packageId) || !lastKnownCurves.has(packageId) || !hasUsableFolderAccess(entry.access)) {
        continue;
      }
      const previousIds = new Set(lastKnownCurves.get(packageId) ?? []);
      let emitted = 0;
      for (const id of entry.curveIds) {
        if (previousIds.has(id) || selfEdits?.has(id) || !previouslySeenCurveIds.has(id)) continue;
        const visibleElsewhere = [...lastKnownCurves].some(([pkg, ids]) => pkg !== packageId && ids.includes(id));
        if (visibleElsewhere) continue;
        if (emitted >= MAX_CURVE_ALERTS_PER_FOLDER) break;
        const product = info[id];
        alerts.push(
          curveMovedIntoFolderAlert(
            { id, name: product?.name ?? "" },
            packageId,
            entry.folderName ?? product?.packageByPackage?.name ?? null,
          ),
        );
        emitted += 1;
      }
    }
  }

  if (reported.length > 0) {
    logger.info("Folder access changes detected", { tags: ["calc-worker"], baseline: lastKnown.size, reported });
  }

  lastKnown.clear();
  lastKnownCurves.clear();
  for (const [packageId, entry] of next) {
    lastKnown.set(packageId, entry.access);
    lastKnownCurves.set(packageId, entry.curveIds);
    if (seenCurveIds) for (const id of entry.curveIds) seenCurveIds.add(id);
  }

  return alerts;
};

/**
 * Creates an observable that emits changed products when product info or configs change
 * for products included in one of the user's pages.
 */
export const createSource = (ctx: ProductChangeAlertsContext): Observable<ProductAlertsTick> => {
  const current = new Map<string, HashEntry>();
  const partialFormulaMetadata = new Map<string, PartialFormulaMetadataEntry>();
  /** Report each off-stream page product once until it reappears in the bulk stream. */
  const missingFromStreamReported = new Set<string>();
  /** Package id changed on the previous tick — helps when the row disappears immediately after a move. */
  const recentPackageChanges = new Set<string>();
  /** Nothing here is comparable across users, so a different id starts from scratch. */
  const resetSessionState = () => {
    current.clear();
    partialFormulaMetadata.clear();
    missingFromStreamReported.clear();
    recentPackageChanges.clear();
    lastKnownFolderAccess.clear();
    expectedOwnEdits.clear();
    lastKnownFolders.clear();
    lastKnownFolderCurves.clear();
    seenCurveIds.clear();
    lastFolderInfo = null;
  };
  /**
   * Last observed folder access per page product. Updated whenever a row is present in the bulk
   * stream; also forced to "none" when a previously tracked row drops out (RLS revoke). Used on
   * the first in-stream appearance only when we saw the row leave — not when page_products beat
   * info for a brand-new curve (see the guard in detectChanges).
   */
  const lastKnownFolderAccess = new Map<string, FolderAccess>();
  /** productId → suppress-until timestamp for saves made in this session. */
  const expectedOwnEdits = new Map<string, number>();
  const liveSelfEdits = (now = Date.now()): Set<string> => {
    const live = new Set<string>();
    for (const [id, expiresAt] of expectedOwnEdits) {
      if (expiresAt > now) live.add(id);
      else expectedOwnEdits.delete(id);
    }
    return live;
  };
  /** Folder-level access, so access changes are caught for folders with no curve on a page. */
  const lastKnownFolders: FolderAccessBaseline = new Map();
  /** Curve ids last seen in each folder — distinguishes a move emptying a folder from a revoke. */
  const lastKnownFolderCurves = new Map<string, string[]>();
  /** Curve ids observed this session, kept after a folder drops so a later reappearance is a move. */
  const seenCurveIds = new Set<string>();
  /** Info payload the folder snapshot was last built from — see the guard in the tick below. */
  let lastFolderInfo: Domain.ProductInfo | null = null;
  /** Monotonic guard: a late-acked deferred save must never clobber a newer snapshot. */
  let snapshotSeq = 0;
  let lastSavedSnapshotSeq = 0;

  const pageProductsFromGraphql$ = ctx.graphql$.pipe(
    switchMap((graphql) => observe(graphql, PageProductIdsSub)),
    map((res) => new Set(res.data?.page_products?.map((p) => p.product_id).filter(Boolean) ?? [])),
  );

  const pageProducts$ = combineLatest([ctx.pageProducts$, pageProductsFromGraphql$]).pipe(
    map(([fromPage, fromSubscription]) => new Set([...fromPage, ...fromSubscription])),
  );

  const ticks$ = (userId: string) =>
    combineLatest([pageProducts$, ctx.info, ctx.configs]).pipe(
      debounceTime(50),
      map(([pageProducts, info, configs]) => {
        const tick = detectChanges(
          current,
          partialFormulaMetadata,
          missingFromStreamReported,
          recentPackageChanges,
          lastKnownFolderAccess,
          pageProducts as Set<string>,
          info,
          configs,
          liveSelfEdits(),
        );
        // Folder access lives entirely in `info`, but this tick also fires for config and page
        // product changes — rebuilding the whole-account snapshot for those would be pure waste.
        if (info === lastFolderInfo) return tick;

        const folderAlerts = detectFolderAccessChanges(
          lastKnownFolders,
          info,
          lastKnownFolderCurves,
          liveSelfEdits(),
          seenCurveIds,
        );
        lastFolderInfo = info;

        // Permission cards are raised by the table watcher — merging them here re-delivers a
        // grant the user may already have dismissed, ~15s after token refresh.
        const workerFolderAlerts = folderAlerts.filter((alert) => alert.changeType === "curve-moved");

        const seq = ++snapshotSeq;
        const snapshot = new Map(lastKnownFolders);
        const save = () => {
          if (seq < lastSavedSnapshotSeq) return;
          lastSavedSnapshotSeq = seq;
          void ctx.folderSnapshots?.save(userId, snapshot);
        };

        // No worker-emitted folder alerts — persist immediately so the stored baseline stays
        // current. This also runs on later info ticks even when a prior alert batch is still
        // unacked, so the deferred commit below is best-effort rather than a hard guarantee.
        if (workerFolderAlerts.length === 0) {
          save();
          return tick;
        }

        // Curve-move alerts from the folder pass: defer the IndexedDB write until the main
        // thread acks this batch.
        return {
          ...tick,
          alerts: mergeAlertsByKey(tick.alerts, workerFolderAlerts),
          commitFolderSnapshot: save,
        };
      }),
      filter((tick) => tick.alerts.length > 0 || tick.debug.length > 0),
    );

  const expectOwnEdits$ = (ctx.expectProductChanges$ ?? EMPTY).pipe(
    tap((payload) => {
      const startedAt = typeof payload.at === "number" ? payload.at : Date.now();
      const expiresAt = startedAt + SELF_EDIT_SUPPRESS_MS;
      for (const id of payload.productIds) {
        if (id) expectedOwnEdits.set(id, expiresAt);
      }
    }),
    ignoreElements(),
  );

  const session$ = ctx.user.pipe(
    map((user) => user.id),
    distinctUntilChanged(),
    // Restore before the first tick, otherwise the boot payload becomes the baseline and any
    // change made while this user was away is silently absorbed into it.
    switchMap((userId) => {
      resetSessionState();
      return from(ctx.folderSnapshots?.load(userId) ?? Promise.resolve(null)).pipe(
        switchMap((restored) => {
          if (restored) for (const [packageId, entry] of restored) lastKnownFolders.set(packageId, entry);
          return ticks$(userId);
        }),
      );
    }),
  );

  return merge(session$, expectOwnEdits$);
};

/** Page-scoped and folder-scoped passes can both flag the same curve; keep one row per key. */
export const mergeAlertsByKey = (primary: ProductChangeAlert[], extra: ProductChangeAlert[]): ProductChangeAlert[] => {
  const combined = extra.length === 0 ? primary : [...primary, ...extra];
  const movedIds = new Set(
    combined.filter((alert) => alert.changeType === "curve-moved" && alert.productId).map((alert) => alert.productId),
  );
  const withoutStalePermission = combined.filter((alert) => {
    if (alert.changeType !== "permissions-changed") return true;
    if (alert.productId && movedIds.has(alert.productId)) return false;
    return true;
  });
  if (extra.length === 0) return withoutStalePermission;
  const seen = new Set<string>();
  const merged: ProductChangeAlert[] = [];
  for (const alert of withoutStalePermission) {
    const key = productChangeSidebarKey(alert);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(alert);
  }
  return merged;
};

/** Detect changes to page products, updating current hash map and returning changed products */
export const detectChanges = (
  current: Map<string, HashEntry>,
  partialFormulaMetadata: Map<string, PartialFormulaMetadataEntry>,
  missingFromStreamReported: Set<string>,
  recentPackageChanges: Set<string>,
  lastKnownFolderAccess: Map<string, FolderAccess>,
  pageProducts: Set<string>,
  info: Domain.ProductInfo,
  configs: Domain.ProductConfigs,
  selfEdits?: ReadonlySet<string>,
): ProductAlertsTick => {
  const alerts: ProductChangeAlert[] = [];
  const debug: CurveFolderDebugPayload[] = [];

  for (const id of pageProducts) {
    const product = info[id];
    const productConfigs = configs[id] ?? [];
    const suppressOwnEdit = selfEdits?.has(id) === true;

    if (!product) {
      const prev = current.get(id);
      if (prev && !missingFromStreamReported.has(id)) {
        missingFromStreamReported.add(id);
        if (!suppressOwnEdit) {
          debug.push(buildMissingFromStreamDebug(id, prev, recentPackageChanges.has(id)));
        }
        recentPackageChanges.delete(id);
      }
      // The row dropped out of the bulk stream entirely (typically full loss of folder access
      // via RLS). Only record "none" when we previously saw this product in the bulk stream —
      // otherwise page_products can land before info (brand-new curve on a page) and we'd
      // treat the later info tick as a permission gain. Folder grants are the table watcher's.
      if (prev) {
        current.set(id, { ...prev, folderAccess: "none", awaitingUsableFormulaBaseline: true });
        lastKnownFolderAccess.set(id, "none");
      }
      continue;
    }

    missingFromStreamReported.delete(id);

    const newMetadata = getMetadataFields(product);
    const newMeta = simpleStableHash(newMetadata);
    const hasConfigs = productConfigs.length > 0;
    const newFormula = simpleStableHash(getFormulaFields(product, productConfigs));
    const newPackage = product.package != null ? String(product.package) : null;
    const newFolderName = product.packageByPackage?.name ?? null;
    const newFolderAccess = getFolderAccess(product);
    const prev = current.get(id);
    const canSeeCurveEdits = hasUsableFolderAccess(newFolderAccess);

    if (prev) {
      const packageChanged = prev.package !== newPackage;
      const permissionChangeDirection = getPermissionChangeDirection(prev.folderAccess, newFolderAccess);

      if (packageChanged) {
        recentPackageChanges.add(id);
        const originVisible = hasVisibleFolderAccess(prev.folderAccess);
        const destinationVisible = hasOriginalFolderAccess(newFolderAccess);
        // Destination may have no usable access (moved into a folder this user cannot read). Still
        // a curve-move: copy names the origin and "a new Folder" when the destination is hidden.
        if (!suppressOwnEdit) {
          alerts.push({
            productId: id,
            name: product.name,
            packageId: newPackage,
            changeType: "curve-moved",
            fromFolderName: originVisible ? prev.folderName : null,
            detail: buildAccessChangeDetail("curve-moved", {
              fromFolderName: originVisible ? prev.folderName : null,
              toFolderName: newFolderName,
              confirmedDestination: destinationVisible,
            }),
          });
        }
      }

      const metaChanged = prev.meta !== newMeta;
      /**
       * Config rows vanish wholesale when folder access is revoked — RLS hides them a tick
       * before the product row reflects the loss, which used to be misreported as a formula
       * edit. No rows means the formula state is unknown, not empty: skip the comparison and
       * keep the last known hash (below), so if rows come back different the edit is still
       * reported, and if they come back identical (access restored) nothing fires.
       */
      const formulaChanged = hasConfigs && prev.formula !== newFormula;
      const accessAlertEmitted = packageChanged || Boolean(permissionChangeDirection);
      // List (and below) must not raise formula/metadata cards — configs are often hidden by RLS,
      // and an upgrade to Read would otherwise dump the current formula as if it had just changed.
      const absorbedUsableGrant = !hasUsableFolderAccess(prev.folderAccess) && canSeeCurveEdits;
      // Configs often land on a later tick than the permission upgrade. The list-era formula
      // hash is frozen (no rows); the first visible configs are a baseline, not an edit.
      const absorbFormulaBaseline = Boolean(prev.awaitingUsableFormulaBaseline) && canSeeCurveEdits && hasConfigs;
      const formulaChangedForAlert = formulaChanged && !absorbedUsableGrant && !absorbFormulaBaseline;
      const metaChangedForAlert = metaChanged && !absorbedUsableGrant;

      if (
        (metaChangedForAlert || formulaChangedForAlert) &&
        !accessAlertEmitted &&
        !suppressOwnEdit &&
        canSeeCurveEdits
      ) {
        const changeType = resolvePartialFormulaMetadataChangeType(partialFormulaMetadata, id, {
          formula: formulaChangedForAlert,
          metadata: metaChangedForAlert,
        });
        if (changeType) {
          const changedFieldLabels = metaChangedForAlert
            ? getChangedMetadataFieldLabels(prev.metadataSnapshot, newMetadata)
            : undefined;
          const metadataDetail =
            changedFieldLabels?.length && (changeType === "metadata" || changeType === "both")
              ? formatMetadataChangeDetail(changedFieldLabels)
              : undefined;

          alerts.push({
            productId: id,
            name: product.name,
            packageId: newPackage,
            changeType,
            detail: metadataDetail,
            changedFieldLabels,
          });
        }
      }
    }

    current.set(id, {
      meta: newMeta,
      metadataSnapshot: newMetadata,
      formula: hasConfigs ? newFormula : (prev?.formula ?? newFormula),
      package: newPackage,
      folderName: newFolderName,
      name: product.name,
      folderAccess: newFolderAccess,
      awaitingUsableFormulaBaseline:
        !canSeeCurveEdits ||
        (!hasConfigs &&
          (prev == null || Boolean(prev.awaitingUsableFormulaBaseline) || !hasUsableFolderAccess(prev.folderAccess))),
    });
    lastKnownFolderAccess.set(id, newFolderAccess);
  }

  return { alerts, debug };
};

/**
 * Product fields editable as curve metadata. Excludes package/folder (separate access alerts) and
 * `has_shared_cell`, which only flips because manual cells were added or removed — that belongs
 * with the formula, see {@link getFormulaFields}.
 */
export const getMetadataFields = (product: TProduct) => {
  const commodityGroup = product.commodityGroupByCommodityGroup;
  return {
    name: product.name,
    description: product.description,
    code: product.code,
    logical_code: product.logical_code,
    uom: product.uom,
    eod_product_dep: product.eod_product_dep,
    summary_type: product.summary_type,
    curve_type: product.curve_type,
    product_type: product.product_type,
    maturity: product.maturity,
    artis_type: product.artis_type,
    commodity_group: product.commodity_group,
    geographical_region: product.geographical_region,
    /** Curve-level Parent — derived from the commodity relation (product has no parent column). */
    parent: commodityGroup
      ? {
          id: commodityGroup.parent,
          name: commodityGroup.commodity_parent_group?.commodity_parent_group ?? null,
        }
      : null,
    /** Relation rows — track these only; scalar ids duplicate the same user edit. */
    commodityGroupByCommodityGroup: commodityGroup
      ? { commodity_group: commodityGroup.commodity_group }
      : commodityGroup,
    geographicalRegionByGeographicalRegion: product.geographicalRegionByGeographicalRegion,
    product_global_dependencies: [...(product.product_global_dependencies ?? [])]
      .map((dep) => dep.global_product)
      .filter(Boolean)
      .sort(),
  };
};

export type MetadataSnapshot = ReturnType<typeof getMetadataFields>;

const METADATA_FIELD_ORDER = [
  "uom",
  "summary_type",
  "name",
  "description",
  "curve_type",
  "product_type",
  "code",
  "logical_code",
  "eod_product_dep",
  "maturity",
  "commodity_group",
  "geographical_region",
  "artis_type",
  "parent",
  "commodityGroupByCommodityGroup",
  "geographicalRegionByGeographicalRegion",
  "product_global_dependencies",
] as const satisfies readonly (keyof MetadataSnapshot)[];

const METADATA_FIELD_LABELS: Record<keyof MetadataSnapshot, string> = {
  name: "Display name",
  description: "Curve name",
  code: "Code",
  logical_code: "Logical code",
  uom: "UoM",
  eod_product_dep: "Snapshot/EOD",
  summary_type: "Aggregation method",
  curve_type: "Curve type",
  product_type: "Product type",
  maturity: "Maturity",
  artis_type: "Artis type",
  commodity_group: "Commodity",
  geographical_region: "Region",
  parent: "Commodity",
  commodityGroupByCommodityGroup: "Commodity",
  geographicalRegionByGeographicalRegion: "Geographical region",
  product_global_dependencies: "Third party feeds",
};

/** Labels for metadata fields that changed between two snapshots. */
export const getChangedMetadataFieldLabels = (prev: MetadataSnapshot, next: MetadataSnapshot): string[] => {
  const changedKeys = METADATA_FIELD_ORDER.filter((key) => JSON.stringify(prev[key]) !== JSON.stringify(next[key]));

  const skipScalars = new Set<keyof MetadataSnapshot>();
  // Parent is no longer a separate UI field (merged into Commodity). The scalar FK
  // duplicates the relation row — keep the user-facing label once.
  if (changedKeys.includes("commodityGroupByCommodityGroup") || changedKeys.includes("parent")) {
    skipScalars.add("commodity_group");
  }
  if (changedKeys.includes("geographicalRegionByGeographicalRegion")) skipScalars.add("geographical_region");

  return [...new Set(changedKeys.filter((key) => !skipScalars.has(key)).map((key) => METADATA_FIELD_LABELS[key]))];
};

/** Figma copy: "Metadata changed: UoM, Aggregation method and 2 other fields". */
export const formatMetadataChangeDetail = (fieldLabels: string[]): string => {
  const unique = [...new Set(fieldLabels.map((label) => label.trim()).filter(Boolean))];
  if (unique.length === 0) return "Metadata changed";
  if (unique.length === 1) return `Metadata changed: ${unique[0]}`;
  if (unique.length === 2) return `Metadata changed: ${unique[0]}, ${unique[1]}`;

  const others = unique.length - 2;
  return `Metadata changed: ${unique[0]}, ${unique[1]} and ${others} other field${others !== 1 ? "s" : ""}`;
};

/**
 * Curve structure, not metadata: the config rows plus `has_shared_cell`. That flag is set for us
 * server-side when a manual (null-formula) month exists, so adding or removing a Manual segment
 * reports as a formula change — and lands in the same hash as the config rows it came with,
 * rather than racing them as a separate "Shared cell" metadata edit.
 */
const getFormulaFields = (product: TProduct, config: TProductConfig[]) => ({
  config: [...config].sort((a, b) => a.offset - b.offset).map((c) => `${c.offset}: ${c.formula ?? "__MANUAL_ENTRY__"}`),
  has_shared_cell: product.has_shared_cell,
});

/**
 * `broadcast` sits below `write` in the notification copy's access ladder: both let a user view
 * and input data, but only `write` also grants edit rights. Ordered low to high: `list` < `read`
 * < `broadcast` < `write`. `none`/`other` mean no visible access at all.
 */
export type FolderAccess = "none" | "list" | "read" | "write" | "broadcast" | "other";

export type LastKnownCurveState = {
  packageId: string | null;
  folderName: string | null;
  folderAccess: FolderAccess;
};

export type OffStreamClassification = {
  accessChangeReason: CurveAccessChangeReason;
  lookupSource: "main-thread-cache" | "by-id-query" | "worker-recent-move" | "none";
  originalFolderAccess?: FolderAccess;
  originalFolderName?: string | null;
  /**
   * The access this classification settled on, for `permissions-changed`. Dropping to `list`
   * leaves the folder visible and gets a card that names it, so the alert cannot assume removal.
   */
  currentFolderAccess?: FolderAccess;
  note: string;
  foundProductSummary?: ReturnType<typeof buildCurveSummary>;
};

export const folderAccessFromPermissions = (
  permissions: ReadonlyArray<{ permission: string }> | null | undefined,
): FolderAccess => {
  if (!permissions?.length) return "none";
  const permission = permissions[0]?.permission;
  if (permission === "write") return "write";
  if (permission === "broadcast") return "broadcast";
  if (permission === "read") return "read";
  if (permission === "list") return "list";
  return "other";
};

/** `write` and `broadcast` both allow viewing and inputting data — see the {@link FolderAccess} doc. */
const isFullFolderAccess = (access: FolderAccess) => access === "write" || access === "broadcast";

const hasUsableFolderAccess = (access: FolderAccess) => access === "read" || isFullFolderAccess(access);

/** List access still shows the folder and its curves, so it counts as "visible" alongside read/write/broadcast. */
const hasVisibleFolderAccess = (access: FolderAccess) => access === "list" || hasUsableFolderAccess(access);

const hasOriginalFolderAccess = (access: FolderAccess | null | undefined) =>
  access != null && hasVisibleFolderAccess(access);

const hasLostFolderAccess = (from: FolderAccess, to: FolderAccess) =>
  hasUsableFolderAccess(from) && !hasUsableFolderAccess(to);

/**
 * Ordered tiers of visible access: invisible (none/other) < list < read < broadcast < write —
 * see {@link FolderAccess}. Comparing tiers lets every move between visible levels resolve to one
 * rule, instead of listing every pair by hand.
 */
const FOLDER_ACCESS_TIER: Record<FolderAccess, number> = {
  none: -1,
  other: -1,
  list: 0,
  read: 1,
  broadcast: 2,
  write: 3,
};

/** Derived from the tier map, which the compiler keeps exhaustive, so no level can be left out. */
export const FOLDER_ACCESS_VALUES = Object.keys(FOLDER_ACCESS_TIER) as FolderAccess[];

export const getPermissionChangeDirection = (
  from: FolderAccess,
  to: FolderAccess,
): PermissionChangeDirection | null => {
  const fromTier = FOLDER_ACCESS_TIER[from];
  const toTier = FOLDER_ACCESS_TIER[to];

  // Becoming visible at all — for the first time — earns the friendly "gained" framing. Once the
  // folder is already visible (list or above), further moves are framed as level changes instead.
  if (fromTier === -1) return toTier === -1 ? null : "gained";
  if (toTier === -1) return "lost";
  if (toTier === fromTier) return null; // from === to: every visible tier is now distinct
  if (toTier === 0) return "restricted"; // dropped to list, but the folder stays visible
  if (toTier < fromTier) return "downgraded"; // e.g. write -> broadcast/read/list
  return "upgraded"; // e.g. list -> read/broadcast/write
};

export const getProductFolderAccess = (product: TProduct): FolderAccess => {
  const perms = product.packageByPackage?.permissions;
  if (!perms?.length) return "none";
  const permission = perms[0]?.permission;
  if (permission === "write") return "write";
  if (permission === "broadcast") return "broadcast";
  if (permission === "read") return "read";
  if (permission === "list") return "list";
  return "other";
};

const getFolderAccess = getProductFolderAccess;

/** Classify a page curve that disappeared from the bulk worker stream (refine on main thread). */
export const classifyOffStreamCurve = (
  lastKnown: LastKnownCurveState,
  found: TProduct | null | undefined,
  lookupSource: OffStreamClassification["lookupSource"],
  originalFolder?: { access: FolderAccess; name: string | null },
): OffStreamClassification => {
  const base = {
    lookupSource,
    originalFolderAccess: originalFolder?.access,
    originalFolderName: originalFolder?.name ?? lastKnown.folderName,
  };

  const foundPackageId = found?.package != null ? String(found.package) : null;
  const productAccess = found ? getFolderAccess(found) : null;

  if (found && foundPackageId && lastKnown.packageId && foundPackageId !== lastKnown.packageId) {
    return {
      ...base,
      accessChangeReason: "curve-moved",
      note: `Curve is now in "${found.packageByPackage?.name ?? foundPackageId}" (was "${lastKnown.folderName ?? lastKnown.packageId}").`,
      foundProductSummary: buildCurveSummary(found.id, found, []),
    };
  }

  if (productAccess && hasLostFolderAccess(lastKnown.folderAccess, productAccess)) {
    return {
      ...base,
      accessChangeReason: "permissions-changed",
      lookupSource,
      currentFolderAccess: productAccess,
      note: `Product permission in folder changed: "${lastKnown.folderAccess}" → "${productAccess}".`,
      foundProductSummary: buildCurveSummary(found!.id, found!, []),
    };
  }

  // Same folder, usable access: the drop from the stream was transient (subscription flap, or a
  // revoke that was re-granted before this lookup ran). Without this check the originalFolder
  // branch below misreads the healthy state as a move and raises a bogus "moved" card.
  if (found && productAccess && hasUsableFolderAccess(productAccess)) {
    return {
      ...base,
      accessChangeReason: "ambiguous",
      note: `Curve is still accessible in "${found.packageByPackage?.name ?? foundPackageId}" with "${productAccess}" access — transient stream drop, nothing to report.`,
      foundProductSummary: buildCurveSummary(found.id, found, []),
    };
  }

  if (originalFolder !== undefined) {
    if (hasOriginalFolderAccess(originalFolder.access)) {
      return {
        ...base,
        accessChangeReason: "curve-moved",
        note:
          `You still have "${originalFolder.access}" access to the original folder "${originalFolder.name ?? lastKnown.packageId}". ` +
          "The curve likely moved to another folder (by-id may still show stale folder data).",
        foundProductSummary: found ? buildCurveSummary(found.id, found, []) : undefined,
      };
    }

    return {
      ...base,
      accessChangeReason: "permissions-changed",
      currentFolderAccess: originalFolder.access,
      note:
        `You no longer have usable access to the original folder "${originalFolder.name ?? lastKnown.packageId}" ` +
        `(access: "${originalFolder.access}").`,
      foundProductSummary: found ? buildCurveSummary(found.id, found, []) : undefined,
    };
  }

  if (!found) {
    return {
      ...base,
      accessChangeReason: "removed-from-subscription",
      note:
        "Not in worker stream or by-id query, and original folder permissions were not loaded. " +
        "Likely moved to an inaccessible folder or deleted.",
    };
  }

  return {
    ...base,
    accessChangeReason: "ambiguous",
    note: `Product still returned in "${found.packageByPackage?.name ?? foundPackageId}" with "${productAccess}" access; could not load original-folder permissions.`,
    foundProductSummary: buildCurveSummary(found.id, found, []),
  };
};

/** Build a user-facing alert after off-stream classification on the main thread. */
export const accessAlertFromOffStreamClassification = (
  payload: Pick<CurveFolderDebugPayload, "productId" | "curveName" | "lastKnownFolder" | "folderAccessChange">,
  classified: OffStreamClassification,
  found: TProduct | null | undefined,
): ProductChangeAlert | null => {
  // Folder permission cards are owned by the table watcher. A 15s-later worker debug payload
  // would otherwise re-deliver a grant or loss the user may already have dismissed.
  if (classified.accessChangeReason !== "curve-moved") return null;

  const fromFolderName = payload.lastKnownFolder?.folderName ?? classified.originalFolderName;
  const fromPackageId = payload.lastKnownFolder?.packageId ?? null;
  const foundPackageId = found?.package != null ? String(found.package) : null;
  const destinationAccess = found ? getFolderAccess(found) : null;
  const confirmedDestination = Boolean(
    foundPackageId && fromPackageId && foundPackageId !== fromPackageId && hasOriginalFolderAccess(destinationAccess),
  );
  const toFolderName = confirmedDestination
    ? (found?.packageByPackage?.name ?? classified.foundProductSummary?.folderName)
    : null;

  return {
    productId: payload.productId,
    name: payload.curveName ?? found?.name ?? "Curve",
    packageId: foundPackageId ?? fromPackageId,
    changeType: "curve-moved",
    fromFolderName,
    detail: buildAccessChangeDetail("curve-moved", {
      fromFolderName,
      toFolderName,
      originalFolderName: classified.originalFolderName ?? fromFolderName,
      confirmedDestination,
    }),
  };
};

const buildCurveSummary = (productId: string, product: TProduct, productConfigs: TProductConfig[]) => ({
  productId,
  id: product.id,
  name: product.name,
  description: product.description,
  packageId: product.package,
  folderName: product.packageByPackage?.name,
  packageType: product.packageByPackage?.package_type,
  permissions: product.packageByPackage?.permissions,
  folderAccess: getFolderAccess(product),
  hasFolderWriteAccess: product.packageByPackage?.permissions?.some((p) => p.permission === "write") ?? false,
  artis_type: product.artis_type,
  curve_type: product.curve_type,
  product_type: product.product_type,
  uom: product.uom,
  eod_product_dep: product.eod_product_dep,
  commodityGroup: product.commodityGroupByCommodityGroup,
  geographicalRegion: product.geographicalRegionByGeographicalRegion,
  has_shared_cell: product.has_shared_cell,
  configSegmentCount: productConfigs.length,
});

const buildMissingFromStreamDebug = (
  productId: string,
  prev: HashEntry,
  sawPackageChangeOnPreviousTick: boolean,
): CurveFolderDebugPayload => ({
  accessChangeReason: sawPackageChangeOnPreviousTick ? "curve-moved" : "removed-from-subscription",
  productId,
  curveName: prev.name,
  lastKnownFolder: { packageId: prev.package, folderName: prev.folderName },
  folderAccessChange: { from: prev.folderAccess, to: "none" },
});
